import React, { useEffect, useRef, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import {
  AlertTriangle, Loader2, Send, GitBranch, Webhook,
  Target, Inbox, Eye, ShieldAlert, ShieldCheck, ArrowRight, TrendingUp, Sparkles, Clock,
  Upload, FileJson, X, Users, Database,
} from "lucide-react";
import {
  SummaryStrip, DataQualityPanel, NextStepsPanel,
  RuntimeSchemaMismatch, AdvancedToolsSection, AdvancedRow,
  SupplierTable, TableControls, applyTableFilters, ActionTooltip,
  computeSummary,
} from "./adminUiHelpers";
import { EvidenceCategoriesPanel } from "../components/EvidenceCategoriesPanel";

const PRIORITY_STYLE = {
  p0: { bg: "rgba(220,38,38,0.06)", stripe: "#dc2626", label: "P0 · URGENT" },
  p1: { bg: "rgba(234,88,12,0.06)", stripe: "#ea580c", label: "P1 · HIGH" },
  p2: { bg: "rgba(255,107,0,0.06)", stripe: "var(--orange)", label: "P2 · GROWTH" },
  p3: { bg: "rgba(59,130,246,0.06)", stripe: "#3b82f6", label: "P3 · FIELD" },
};

export default function Admin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dash, setDash] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [pinging, setPinging] = useState(false);
  const [pingResult, setPingResult] = useState(null);
  const [verifStats, setVerifStats] = useState(null);
  const [recomputing, setRecomputing] = useState(false);
  const [recomputeResult, setRecomputeResult] = useState(null);
  const [unpublishingOrphans, setUnpublishingOrphans] = useState(false);
  const [orphanResult, setOrphanResult] = useState(null);
  const [importingImmex, setImportingImmex] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // iter 37 — file-based IMMEX import modal state
  const [immexModalOpen, setImmexModalOpen] = useState(false);
  const [immexFileName, setImmexFileName] = useState("");
  const [immexPayload, setImmexPayload] = useState(null);
  const [immexPreview, setImmexPreview] = useState(null);
  const [immexPreviewing, setImmexPreviewing] = useState(false);
  const [immexDragOver, setImmexDragOver] = useState(false);
  const [immexError, setImmexError] = useState("");
  const immexFileInputRef = useRef(null);
  const [unpublishingFakes, setUnpublishingFakes] = useState(false);
  const [fakeEmailsResult, setFakeEmailsResult] = useState(null);
  const [indexnowRunning, setIndexnowRunning] = useState(false);
  const [indexnowResult, setIndexnowResult] = useState(null);
  // iter47 UX — supplier table controls and view mode
  const [tableMode, setTableMode] = useState("grouped");
  const [tableQuery, setTableQuery] = useState("");
  const [tableStatus, setTableStatus] = useState("");

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/admin/manufacturers"); setItems(data); }
    finally { setLoading(false); }
  }
  async function loadDash() {
    setDashLoading(true);
    try { const { data } = await api.get("/admin/dashboard"); setDash(data); }
    catch { /* keep prior dash on transient errors */ }
    finally { setDashLoading(false); }
  }
  async function loadVerifStats() {
    try { const { data } = await api.get("/admin/verification-stats"); setVerifStats(data); }
    catch { /* keep prior stats on transient errors */ }
  }
  useEffect(() => { load(); loadDash(); loadVerifStats(); }, []);

  async function toggle(id, key, value) {
    await api.patch(`/admin/manufacturers/${id}`, { [key]: value });
    load();
    if (key === "verified") loadVerifStats();
  }

  async function recomputeVerified() {
    setRecomputing(true); setRecomputeResult(null);
    try {
      const { data } = await api.post("/admin/manufacturers/recompute-verified");
      setRecomputeResult(data);
      load();
      loadVerifStats();
    } catch (e) {
      setRecomputeResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setRecomputing(false);
    }
  }

  async function unpublishOrphans() {
    // Destructive: get explicit confirmation before running the write path.
    const ok = window.confirm(
      "Unpublish all orphan manufacturer listings?\n\n" +
      "This will set published=false on every manufacturer_profile whose " +
      "user_id does not match an active user. Records stay in the database " +
      "and can be re-published later, but they will disappear from the " +
      "public directory immediately."
    );
    if (!ok) return;
    setUnpublishingOrphans(true); setOrphanResult(null);
    try {
      const { data } = await api.post("/admin/unpublish-orphans?dry_run=false");
      setOrphanResult(data);
      window.alert(
        `Orphan cleanup complete.\n\n` +
        `Scanned: ${data.scanned}\n` +
        `Orphans found: ${data.orphans_found}\n` +
        `Unpublished this run: ${data.unpublished}\n` +
        `Already unpublished (skipped): ${data.skipped_already_unpublished}\n` +
        `Valid users in DB: ${data.valid_user_count}`
      );
      load();
      loadVerifStats();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setOrphanResult({ error: msg });
      window.alert(`Orphan cleanup failed: ${msg}`);
    } finally {
      setUnpublishingOrphans(false);
    }
  }

  async function submitToIndexNow() {
    const ok = window.confirm(
      "Submit every canonical public URL to IndexNow?\n\n" +
      "Pings Bing / Yandex / IndexNow-participating engines with the " +
      "full sitemap: home, capability pages, and every published " +
      "supplier's CANONICAL /m/{slug} URL (legacy /suppliers/ and " +
      "/plants/ aliases are excluded — they 301-redirect to /m/{slug} " +
      "and submitting them triggers Bing's UserForbiddenToAccessSite). " +
      "Batched at 10,000 URLs per submission. Safe to re-run."
    );
    if (!ok) return;
    setIndexnowRunning(true); setIndexnowResult(null);
    try {
      const { data } = await api.post("/admin/indexnow/bootstrap");
      setIndexnowResult(data);
      window.alert(
        `IndexNow submission complete.\n\n` +
        `Host: ${data.host}\n` +
        `Total URLs: ${data.total_urls}\n` +
        `Submitted: ${data.submitted}\n` +
        `Batches: ${data.batches} (${data.batch_size} URLs/batch)\n` +
        `Last status: ${data.last_status}\n` +
        (data.error ? `Error: ${data.error}` : "")
      );
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setIndexnowResult({ error: msg });
      window.alert(`IndexNow submission failed: ${msg}`);
    } finally {
      setIndexnowRunning(false);
    }
  }

  async function unpublishFakeEmails() {
    // Same throttling as the orphan sweep: confirm first, popup after.
    const ok = window.confirm(
      "Unpublish all demo / test / example manufacturer listings?\n\n" +
      "This scans every profile and matches on BOTH contact_email and " +
      "the linked user's email. Any address whose local-part starts with " +
      "demo, test, example, sample, or fake — or whose domain is one of " +
      "example.com / example.org / test.com — will be unpublished.\n\n" +
      "Records stay in the database and can be re-published later."
    );
    if (!ok) return;
    setUnpublishingFakes(true); setFakeEmailsResult(null);
    try {
      const { data } = await api.post("/admin/unpublish-fake-emails?dry_run=false");
      setFakeEmailsResult(data);
      window.alert(
        `Demo/test cleanup complete.\n\n` +
        `Scanned: ${data.scanned}\n` +
        `Matched: ${data.matched}\n` +
        `Unpublished this run: ${data.unpublished}\n` +
        `Already unpublished (skipped): ${data.already_unpublished}\n` +
        `Prefixes: ${data.prefixes?.join(", ")}\n` +
        `Domains: ${data.domains?.join(", ")}`
      );
      load();
      loadVerifStats();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setFakeEmailsResult({ error: msg });
      window.alert(`Demo/test cleanup failed: ${msg}`);
    } finally {
      setUnpublishingFakes(false);
    }
  }

  // iter 37 — file-based IMMEX import flow.
  // The "Import IMMEX Registry Data" button now opens a modal with
  // drag-and-drop file input, runs a dry-run against the API to
  // preview counts + sample slugs, and only commits the real write
  // after the admin clicks "Confirm import".
  function openImmexModal() {
    setImmexModalOpen(true);
    setImmexFileName("");
    setImmexPayload(null);
    setImmexPreview(null);
    setImmexError("");
    setImportResult(null);
  }

  function closeImmexModal() {
    if (importingImmex || immexPreviewing) return;
    setImmexModalOpen(false);
  }

  async function loadImmexFile(file) {
    setImmexError("");
    setImmexPayload(null);
    setImmexPreview(null);
    setImmexFileName(file?.name || "");
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      setImmexError("Only .json files are accepted.");
      return;
    }
    let text;
    try {
      text = await file.text();
    } catch (e) {
      setImmexError(`Could not read file: ${e.message}`);
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setImmexError(`Invalid JSON: ${e.message}`);
      return;
    }
    if (!Array.isArray(parsed)) {
      setImmexError("File must contain a JSON array of manufacturer records.");
      return;
    }
    setImmexPayload(parsed);
    // Immediately dry-run so the admin sees the preview without a
    // second click.
    setImmexPreviewing(true);
    try {
      const { data } = await postWithAuthRetry(
        "/admin/import-immex-seed?dry_run=true",
        parsed
      );
      setImmexPreview(data);
    } catch (e) {
      setImmexError(readAuthErr(e));
    } finally {
      setImmexPreviewing(false);
    }
  }

  // iter46 — helper: axios POST with one automatic retry if the first
  // attempt returns 401. Between attempts we re-run /auth/me to force
  // AuthContext to prove the session is still live (and re-attach a
  // fresh Bearer if the localStorage token was rotated). Fixes the
  // "Not authenticated" bug where a signed-in admin's dashboard was
  // hydrated from a stale cookie but the JWT expired mid-session.
  async function postWithAuthRetry(url, body) {
    try {
      return await api.post(url, body);
    } catch (e) {
      if (e.response?.status !== 401) throw e;
      // Try to prove auth is live; if this succeeds the retry will too.
      try {
        await api.get("/auth/me");
      } catch (_) {
        // Genuine sign-out — surface the original error.
        throw e;
      }
      return await api.post(url, body);
    }
  }

  function readAuthErr(e) {
    const d = e.response?.data?.detail;
    if (d && typeof d === "object" && d.message) return d.message;
    if (typeof d === "string") return d;
    return e.message || "Request failed";
  }

  function onImmexDrop(evt) {
    evt.preventDefault();
    setImmexDragOver(false);
    const file = evt.dataTransfer?.files?.[0];
    if (file) loadImmexFile(file);
  }

  async function confirmImmexImport() {
    if (!immexPayload || !immexPreview) return;
    setImportingImmex(true);
    try {
      const { data } = await postWithAuthRetry(
        "/admin/import-immex-seed?dry_run=false",
        immexPayload
      );
      setImportResult(data);
      setImmexModalOpen(false);
      window.alert(
        `IMMEX import complete.\n\n` +
        `Received: ${data.received}\n` +
        `Inserted: ${data.inserted}\n` +
        `Updated (unclaimed): ${data.updated}\n` +
        `Skipped (already claimed): ${data.skipped_claimed}\n` +
        `Skipped (no slug): ${data.skipped_no_slug}\n` +
        `Errors: ${data.errors?.length || 0}`
      );
      load();
      loadVerifStats();
    } catch (e) {
      const msg = readAuthErr(e);
      setImportResult({ error: msg });
      setImmexError(msg);
    } finally {
      setImportingImmex(false);
    }
  }

  async function runExpirationCheck() {
    setScanning(true); setScanResult(null);
    try {
      const { data } = await api.post("/admin/run-expiration-check");
      setScanResult(data);
    } catch (e) {
      setScanResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setScanning(false);
    }
  }

  async function pingIndexNow() {
    setPinging(true); setPingResult(null);
    try {
      const { data } = await api.post("/admin/indexnow/submit");
      setPingResult(data);
    } catch (e) {
      setPingResult({ error: e.response?.data?.detail || e.message });
    } finally {
      setPinging(false);
    }
  }

  return (
    <div className="container-cd py-12" data-testid="admin-page">
      <div className="overline">ADMIN · CORRIDOR · COMMAND CENTER</div>
      <h1 className="font-display text-5xl mt-3 leading-[0.9]" data-testid="admin-title">
        Today
      </h1>
      <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
        Your daily ops dashboard. Compliance, RFQs, plant views — and one mission for today.
      </p>

      {/* COMPLIANCE VAULT — evidence-based Corridor Verified ceremony entry */}
      <a
        href="/admin/vault"
        data-testid="admin-vault-entry"
        className="mt-5 flex items-center justify-between gap-4 rounded-lg p-4 no-underline transition-colors"
        style={{
          border: "1px solid var(--line)",
          background: "rgba(22,163,74,0.06)",
          color: "inherit",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center"
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              background: "rgba(22,163,74,0.12)",
            }}
          >
            <ShieldCheck size={20} style={{ color: "var(--green)" }} />
          </div>
          <div>
            <div className="overline" style={{ color: "var(--green)" }}>
              COMPLIANCE VAULT
            </div>
            <div className="font-display text-lg leading-tight mt-0.5">
              Evidence-based Corridor Verified ceremony
            </div>
            <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
              Upload · review · approve evidence per exact plant · grant buyer access · audit trail
            </div>
          </div>
        </div>
        <div
          className="font-mono text-xs flex items-center gap-1"
          style={{ color: "var(--green)" }}
        >
          Open Vault <ArrowRight size={14} />
        </div>
      </a>

      {/* EVIDENCE CATEGORIES — dry-run / receipt-gated apply / rollback */}
      <EvidenceCategoriesPanel />

      {/* TODAY'S MISSION */}
      {dash?.todays_mission && (
        <MissionCard mission={dash.todays_mission} loading={dashLoading} />
      )}

      {/* iter47 UX — Runtime / schema mismatch safety banner (only appears if
          the runtime cannot read backfilled fields on live IMMEX rows). */}
      {(() => {
        const s = computeSummary(items);
        return (
          <RuntimeSchemaMismatch
            immexCount={s.immex}
            backfilledCount={s.backfilled}
          />
        );
      })()}

      {/* iter47 UX — Plain-English summary strip: 8 counters + hover tooltips */}
      {!loading ? <SummaryStrip items={items} /> : null}

      {/* iter47 UX — What should I do next? Safe daily workflow */}
      <NextStepsPanel />

      {/* iter47 UX — Data Quality (read-only counters only) */}
      {!loading ? <DataQualityPanel items={items} /> : null}

      {/* STATS ROW */}
      <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="admin-stats-row">
        <StatCard
          icon={GitBranch}
          label="STAGE"
          value={dash?.roadmap?.stage ?? "—"}
          sub={dash?.roadmap?.stage_name}
          accent="var(--orange)"
          testid="admin-stat-stage"
          to="/admin/roadmap"
        />
        <StatCard
          icon={TrendingUp}
          label="PLANTS"
          value={dash?.roadmap?.plant_count ?? "—"}
          sub={
            dash?.roadmap?.next_stage
              ? `${dash.roadmap.plants_to_next_stage} → Stage ${dash.roadmap.next_stage}`
              : "Max stage"
          }
          accent="var(--green)"
          testid="admin-stat-plants"
          to="/admin/roadmap"
        />
        <StatCard
          icon={Inbox}
          label="RFQs UNREPLIED"
          value={dash?.rfqs?.unreplied ?? 0}
          sub={`of ${dash?.rfqs?.total ?? 0} total`}
          accent={dash?.rfqs?.unreplied > 0 ? "#ea580c" : "var(--ink-2)"}
          testid="admin-stat-rfqs"
          to="/inbox"
        />
        <StatCard
          icon={ShieldAlert}
          label="COMPLIANCE"
          value={dash?.compliance_alerts_pending?.length ?? 0}
          sub="certs expiring ≤30d"
          accent={dash?.compliance_alerts_pending?.length > 0 ? "#dc2626" : "var(--ink-2)"}
          testid="admin-stat-compliance"
          to="/admin/roadmap"
        />
      </section>

      {/* VERIFICATION STATS — iter28: verified vs unverified counts + manual recompute */}
      <section
        className="mt-6 cd-card p-5"
        data-testid="admin-verification-panel"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div
              className="flex items-center justify-center"
              style={{
                width: 40, height: 40, borderRadius: 8,
                background: "rgba(22,163,74,0.08)",
              }}
            >
              <ShieldCheck size={20} style={{ color: "var(--green)" }} />
            </div>
            <div>
              <div className="overline">VERIFICATION · MANUFACTURER DATABASE</div>
              <div className="mt-1 font-display text-2xl leading-none" data-testid="admin-verif-headline">
                <span style={{ color: "var(--green)" }} data-testid="admin-verif-verified-count">
                  {verifStats?.verified ?? "—"}
                </span>
                <span className="text-sm font-mono mx-2" style={{ color: "var(--ink-2)" }}>
                  /
                </span>
                <span data-testid="admin-verif-total">{verifStats?.total ?? "—"}</span>
                <span className="text-sm font-mono ml-2" style={{ color: "var(--ink-2)" }}>
                  verified ({verifStats?.verified_pct ?? 0}%)
                </span>
              </div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                <span data-testid="admin-verif-unverified-count">
                  {verifStats?.unverified ?? 0}
                </span>
                {" "}unverified
                {verifStats?.manually_overridden ? (
                  <span> · <span data-testid="admin-verif-overrides">{verifStats.manually_overridden}</span> manual overrides</span>
                ) : null}
              </div>
            </div>
          </div>
          <div
            className="font-mono text-xs"
            style={{ color: "var(--ink-2)", maxWidth: 380 }}
            data-testid="admin-verif-tools-moved"
          >
            Bulk verification and cleanup tools have moved into{" "}
            <strong style={{ color: "#dc2626" }}>Advanced / Owner-only tools</strong>{" "}
            (bottom of page). Not part of the daily workflow — see the four-step
            guidance at the top.
          </div>
        </div>

        {/* Failure breakdown */}
        {verifStats?.failure_breakdown ? (
          <div
            className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2 font-mono text-xs"
            data-testid="admin-verif-breakdown"
          >
            <BreakdownChip
              label="Free email"
              value={verifStats.failure_breakdown.free_email_domain}
              testid="admin-verif-failure-free-email"
            />
            <BreakdownChip
              label="Missing phone"
              value={verifStats.failure_breakdown.missing_phone}
              testid="admin-verif-failure-phone"
            />
            <BreakdownChip
              label="Missing website"
              value={verifStats.failure_breakdown.missing_website}
              testid="admin-verif-failure-website"
            />
            <BreakdownChip
              label="Missing email"
              value={verifStats.failure_breakdown.missing_email}
              testid="admin-verif-failure-email"
            />
          </div>
        ) : null}
      </section>


      {/* TWO-COLUMN PANELS */}
      <section className="mt-6 grid lg:grid-cols-2 gap-4" data-testid="admin-panels">
        {/* Recent RFQs */}
        <Panel
          title="Recent RFQs"
          icon={Inbox}
          link={{ to: "/inbox", label: "Open inbox →" }}
          testid="admin-panel-rfqs"
        >
          {!dash?.rfqs?.recent?.length ? (
            <Empty label="No RFQs yet — share a plant profile or paid microsite to start the flow." />
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
              {dash.rfqs.recent.map((r) => (
                <li
                  key={r.inquiry_id}
                  className="py-3 flex items-start gap-3"
                  data-testid={`admin-rfq-row-${r.inquiry_id}`}
                >
                  <StatusDot status={r.status} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{r.subject || "—"}</div>
                    <div className="font-mono text-xs mt-0.5" style={{ color: "var(--ink-2)" }}>
                      {r.buyer_name || "?"}
                      {r.buyer_company ? ` · ${r.buyer_company}` : ""}
                      {r.manufacturer_slug ? ` → ${r.manufacturer_slug}` : ""}
                    </div>
                  </div>
                  <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                    {formatAgo(r.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        {/* Top-viewed plants this week */}
        <Panel
          title="Top-viewed plants · 7d"
          icon={Eye}
          link={{ to: "/plants", label: "Open directory →" }}
          testid="admin-panel-views"
        >
          {!dash?.top_viewed_plants?.length ? (
            <Empty label="No views logged yet. Share /plants/{slug}?ref=derick to start the count." />
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
              {dash.top_viewed_plants.map((p) => (
                <li
                  key={p.slug}
                  className="py-3 flex items-center gap-3"
                  data-testid={`admin-viewed-row-${p.slug}`}
                >
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/plants/${p.slug}`}
                      className="font-semibold hover:opacity-80 truncate block"
                    >
                      {p.plant_name}
                    </Link>
                    <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                      {p.industrial_park || "—"} · {p.capability || "—"}
                    </div>
                  </div>
                  <span
                    className="font-display text-2xl"
                    style={{ color: "var(--orange)" }}
                    data-testid={`admin-view-count-${p.slug}`}
                  >
                    {p.view_count_7d}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </section>

      {/* COMPLIANCE PANEL — full width */}
      {dash?.compliance_alerts_pending?.length > 0 && (
        <Panel
          title="Compliance alerts pending"
          icon={ShieldAlert}
          accent="#dc2626"
          link={{ to: "/admin/roadmap", label: "Manage in roadmap →" }}
          testid="admin-panel-compliance"
          className="mt-4"
        >
          <ul className="divide-y" style={{ borderColor: "var(--line)" }}>
            {dash.compliance_alerts_pending.map((c) => (
              <li
                key={c.slug}
                className="py-3 flex flex-wrap items-center gap-3"
                data-testid={`admin-compliance-row-${c.slug}`}
              >
                <Link
                  to={`/plants/${c.slug}`}
                  className="font-semibold hover:opacity-80 flex-1 min-w-[140px]"
                >
                  {c.plant_name}
                </Link>
                <span className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                  {c.immex_cert_number || "—"}
                </span>
                <span
                  className="cd-chip"
                  style={{
                    background: (c.days_until_expiry ?? 99) <= 7 ? "#dc2626" : "var(--orange)",
                    color: "white",
                    fontSize: 11,
                  }}
                >
                  {c.days_until_expiry != null
                    ? `${c.days_until_expiry}d`
                    : "—"}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* QUICK NAV — admin sub-pages */}
      <section className="mt-8 grid md:grid-cols-2 gap-3" data-testid="admin-quicknav">
        <Link
          to="/admin/roadmap"
          className="cd-card p-5 flex items-center justify-between gap-4 hover:opacity-90"
          data-testid="admin-roadmap-link"
        >
          <div>
            <div className="overline" style={{ color: "var(--orange)" }}>
              <GitBranch size={11} className="inline mr-2 -mt-1" /> STAGED ROADMAP
            </div>
            <p className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>
              Plant-count gated feature unlocks · tier &amp; INDEX toggles · compliance scanner
            </p>
          </div>
          <ArrowRight size={16} />
        </Link>

        <Link
          to="/admin/webhooks"
          className="cd-card p-5 flex items-center justify-between gap-4 hover:opacity-90"
          data-testid="admin-webhooks-link"
        >
          <div>
            <div className="overline" style={{ color: "#3b82f6" }}>
              <Webhook size={11} className="inline mr-2 -mt-1" /> WEBHOOK CATALOG
            </div>
            <p className="text-sm mt-2" style={{ color: "var(--ink-2)" }}>
              Sample payloads + send-test buttons for every event flowing to Make.com
            </p>
          </div>
          <ArrowRight size={16} />
        </Link>
      </section>

      {/* ADMIN MAP — the 12 functional groups and where each lives (V1 refresh) */}
      <section className="mt-8 cd-card p-5" data-testid="admin-map">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="overline" style={{ color: "var(--orange)" }}>ADMIN MAP · 12 GROUPS</div>
          <Link to="/admin/controls" className="font-mono text-xs underline" data-testid="admin-controls-link">
            Full controls reference (every endpoint documented) →
          </Link>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {[
            ["1 · Command Center", "/admin", "Today's mission + stats"],
            ["2 · Registry & Manufacturers", "/admin", "Records, verification stats, hygiene"],
            ["3 · Suppliers & Imports", "/admin/suppliers", "CSV intake, history, export"],
            ["4 · Compliance Vault", "/admin/vault", "Evidence docs + Verified ceremony"],
            ["5 · Vault Access Requests", "/admin/vault", "Buyer evidence access"],
            ["6 · Claims & Submissions", "/admin/vault", "Claim/confirm queue"],
            ["7 · Evidence Categories", "/admin", "81-taxonomy, dry-run/apply/rollback"],
            ["8 · SEO & Indexing", "/admin/seo", "Bing stats + IndexNow"],
            ["9 · Webhooks", "/admin/webhooks", "Make.com catalog + tests"],
            ["10 · Notifications & Email", "/admin", "Queue, expiry scanner"],
            ["11 · Plants Ops & Roadmap", "/admin/roadmap", "Stage gates, tiers, compliance"],
            ["12 · Leads & Growth", "/admin", "Founding + Enterprise leads"],
          ].map(([name, route, blurb], i) => (
            <Link
              key={i}
              to={route}
              className="p-3 block hover:opacity-80"
              style={{ border: "1px solid var(--line)", background: "white" }}
              data-testid={`admin-map-group-${i + 1}`}
            >
              <div className="font-mono text-xs font-semibold" style={{ color: "var(--ink)" }}>{name}</div>
              <div className="font-mono mt-1" style={{ fontSize: 10, color: "var(--ink-2)" }}>{blurb}</div>
              <div className="font-mono mt-1" style={{ fontSize: 10, color: "var(--orange)" }}>{route}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Expiration scanner — safe daily action, stays in the main flow. */}
      <div
        className="mt-8 cd-card p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
        data-testid="admin-expiration-scanner"
      >
        <div>
          <div className="overline" style={{ color: "var(--orange)" }}>
            <AlertTriangle size={11} className="inline mr-2 -mt-1" /> EXPIRATION SCANNER
            <ActionTooltip
              what="Scans every compliance document on the platform and queues an alert email for any expiring within 30 days (or already expired). Will not re-alert for 7 days."
              changesData={false}
              sendsEmail={true}
              sendsIndexNow={false}
              undoable={false}
              backupRequired={false}
              testid="tip-expiration-scanner"
            />
          </div>
          <p className="text-sm mt-2" style={{ color: "var(--ink-2)", maxWidth: 540 }}>
            Reads compliance docs, queues email alerts for certs expiring soon.
            Safe to run daily. Does not change data or publication.
          </p>
          {scanResult && (
            <div className="mt-3 font-mono text-xs" data-testid="scan-result">
              {scanResult.error ? (
                <span style={{ color: "var(--red)" }}>Error: {scanResult.error}</span>
              ) : (
                <span>Scanned {scanResult.docs_scanned} docs · Queued {scanResult.alerts_sent} alerts</span>
              )}
            </div>
          )}
        </div>
        <button
          onClick={runExpirationCheck}
          disabled={scanning}
          className="btn btn-primary"
          data-testid="run-expiration-check-btn"
        >
          {scanning ? <><Loader2 size={14} className="animate-spin" /> Scanning…</> : "Run expiration check"}
        </button>
      </div>

      {/* iter47 UX — redesigned supplier table: grouped by legal entity by
          default with a flat-list toggle. Every plant remains its own row
          and every row shows plant_location_id, address, IMMEX ref/year,
          and truth badges. Legacy verify/feature toggles are kept but
          re-labelled. */}
      <section className="mt-10" data-testid="admin-supplier-directory">
        <div className="overline">MANUFACTURER DIRECTORY · {items.length.toLocaleString()}</div>
        {loading ? (
          <div className="overline mt-6">LOADING…</div>
        ) : (
          <>
            <TableControls
              mode={tableMode}
              setMode={setTableMode}
              query={tableQuery}
              setQuery={setTableQuery}
              statusFilter={tableStatus}
              setStatusFilter={setTableStatus}
              count={items.length}
            />
            <div className="mt-3">
              <SupplierTable
                items={applyTableFilters(items, { query: tableQuery, statusFilter: tableStatus })}
                mode={tableMode}
                onLegacyVerify={(r) => r /* legacy verify is hard-blocked — TableRow renders the ceremony link */}
                onLegacyFeatured={(r) => toggle(r.profile_id, "featured", !r.featured)}
              />
            </div>
            <div
              className="mt-3 font-mono text-xs flex flex-wrap items-center gap-3"
              style={{ color: "var(--ink-2)" }}
            >
              <span>
                Need to edit a supplier? Open the full drawer editor:
              </span>
              <Link
                to="/admin/suppliers"
                className="btn btn-ghost"
                style={{ padding: "3px 10px", fontSize: 11, minHeight: 0 }}
                data-testid="admin-open-supplier-drawer"
              >
                <Users size={11} strokeWidth={1.8} />
                <span style={{ marginLeft: 4 }}>Open supplier editor →</span>
              </Link>
              <Link
                to="/admin/seo"
                className="btn btn-ghost"
                style={{ padding: "3px 10px", fontSize: 11, minHeight: 0 }}
              >
                SEO dashboard →
              </Link>
            </div>
          </>
        )}
      </section>


      {/* iter47 UX — Advanced / Owner-only tools · collapsed, red UNSAFE.
          Each entry uses the SAME existing handler as before — behavior is
          unchanged, only the presentation, labels and tooltips are new. */}
      <AdvancedToolsSection>
        <AdvancedRow
          title="Recompute Verified (bulk)"
          tooltipProps={{
            what: "Re-runs the legacy verification rules across every manufacturer and flips 'verified' true/false in bulk. Not evidence-based. Do not use once the evidence-based verification ceremony ships.",
            changesData: true, sendsEmail: false, sendsIndexNow: false,
            undoable: false, backupRequired: true,
            testid: "tip-recompute-verified",
          }}
          action={(
            <button
              type="button"
              onClick={recomputeVerified}
              disabled={recomputing}
              className="btn btn-dark"
              data-testid="admin-verif-recompute-btn"
            >
              {recomputing ? <><Loader2 size={14} className="animate-spin" /> Recomputing…</> : "Run · UNSAFE"}
            </button>
          )}
          resultLine={recomputeResult
            ? (recomputeResult.error
                ? `Error: ${recomputeResult.error}`
                : `Flipped ${recomputeResult.flipped_to_verified} → verified, ${recomputeResult.flipped_to_unverified} → unverified, ${recomputeResult.unchanged} unchanged.`)
            : null}
        />
        <AdvancedRow
          title="Legacy CSV Import (IMMEX registry)"
          tooltipProps={{
            what: "Old CSV-based IMMEX importer. Historically hardcoded 'verified=true' on rows it inserted. Kept for audit but should NOT be used against the audited Juárez 636 CSV — use the receipt-gated corridor upsert instead.",
            changesData: true, sendsEmail: false, sendsIndexNow: false,
            undoable: false, backupRequired: true,
            testid: "tip-legacy-immex-import",
          }}
          action={(
            <button
              type="button"
              onClick={openImmexModal}
              disabled={importingImmex}
              className="btn btn-dark"
              data-testid="admin-import-immex-seed-btn"
            >
              {importingImmex
                ? <><Loader2 size={14} className="animate-spin" /> Importing…</>
                : "Open legacy importer · UNSAFE"}
            </button>
          )}
          resultLine={importResult
            ? (importResult.error
                ? `Error: ${importResult.error}`
                : `Received ${importResult.received}: ${importResult.inserted} inserted, ${importResult.updated} updated, ${importResult.skipped_claimed} skipped (claimed), ${importResult.skipped_no_slug} skipped (no slug).`)
            : null}
        />
        <AdvancedRow
          title="Unpublish orphan listings"
          tooltipProps={{
            what: "Sets published=false on every profile whose user_id does not match an active user account. Records stay in the database and can be re-published later, but they disappear from the public directory immediately.",
            changesData: true, sendsEmail: false, sendsIndexNow: false,
            undoable: true, backupRequired: true,
            testid: "tip-unpublish-orphans",
          }}
          action={(
            <button
              type="button"
              onClick={unpublishOrphans}
              disabled={unpublishingOrphans}
              className="btn btn-dark"
              data-testid="admin-unpublish-orphans-btn"
            >
              {unpublishingOrphans
                ? <><Loader2 size={14} className="animate-spin" /> Unpublishing…</>
                : "Run · UNSAFE"}
            </button>
          )}
          resultLine={orphanResult
            ? (orphanResult.error
                ? `Error: ${orphanResult.error}`
                : `Scanned ${orphanResult.scanned}, found ${orphanResult.orphans_found} orphans, unpublished ${orphanResult.unpublished} this run.`)
            : null}
        />
        <AdvancedRow
          title="Unpublish demo / test / example email listings"
          tooltipProps={{
            what: "Sets published=false on profiles with a demo/test/example/sample/fake email address (either on the profile or the linked user). Reversible.",
            changesData: true, sendsEmail: false, sendsIndexNow: false,
            undoable: true, backupRequired: false,
            testid: "tip-unpublish-demo-test",
          }}
          action={(
            <button
              type="button"
              onClick={unpublishFakeEmails}
              disabled={unpublishingFakes}
              className="btn btn-dark"
              data-testid="admin-unpublish-fake-emails-btn"
            >
              {unpublishingFakes
                ? <><Loader2 size={14} className="animate-spin" /> Unpublishing…</>
                : "Run · UNSAFE"}
            </button>
          )}
          resultLine={fakeEmailsResult
            ? (fakeEmailsResult.error
                ? `Error: ${fakeEmailsResult.error}`
                : `Scanned ${fakeEmailsResult.scanned}, matched ${fakeEmailsResult.matched} demo/test emails, unpublished ${fakeEmailsResult.unpublished} this run.`)
            : null}
        />
        <AdvancedRow
          title="Submit ALL canonical URLs to IndexNow"
          tooltipProps={{
            what: "Pings Bing/Yandex/Seznam/Naver with the full sitemap (home, capability pages, every published /m/{slug}). Google does not participate. Safe to re-run but visible to search engines — do not use during a data backfill.",
            changesData: false, sendsEmail: false, sendsIndexNow: true,
            undoable: false, backupRequired: false,
            testid: "tip-indexnow-submit-all",
          }}
          action={(
            <button
              type="button"
              onClick={submitToIndexNow}
              disabled={indexnowRunning}
              className="btn btn-dark"
              data-testid="admin-indexnow-submit-btn"
            >
              {indexnowRunning
                ? <><Loader2 size={14} className="animate-spin" /> Submitting…</>
                : "Submit ALL · UNSAFE"}
            </button>
          )}
          resultLine={indexnowResult
            ? (indexnowResult.error
                ? `Error: ${indexnowResult.error}`
                : `IndexNow: ${indexnowResult.submitted}/${indexnowResult.total_urls} URLs submitted in ${indexnowResult.batches} batch(es) to ${indexnowResult.host}.`)
            : null}
        />
        <AdvancedRow
          title="Ping IndexNow (single sitemap.xml)"
          tooltipProps={{
            what: "One-shot ping of the current sitemap URL to Bing/Yandex/etc. Announces the sitemap URL; the engines then re-crawl on their own schedule.",
            changesData: false, sendsEmail: false, sendsIndexNow: true,
            undoable: false, backupRequired: false,
            testid: "tip-indexnow-ping",
          }}
          action={(
            <button
              onClick={pingIndexNow}
              disabled={pinging}
              className="btn btn-dark"
              data-testid="indexnow-submit-btn"
            >
              {pinging ? <><Loader2 size={14} className="animate-spin" /> Pinging…</> : "Ping · UNSAFE"}
            </button>
          )}
          resultLine={pingResult
            ? (pingResult.error
                ? `Error: ${pingResult.error}`
                : `Submitted ${pingResult.submitted} URLs · host ${pingResult.host} · status ${pingResult.status}.`)
            : null}
        />
        <div
          className="mt-4 font-mono text-xs"
          style={{ color: "var(--ink-2)", borderTop: "1px solid var(--line)", paddingTop: 12 }}
        >
          Google does NOT participate in IndexNow.{" "}
          Submit your sitemap once at{" "}
          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noreferrer"
            className="underline"
            data-testid="gsc-link"
          >
            Google Search Console
          </a>{" "}
          (Indexing → Sitemaps → paste <code>/api/sitemap.xml</code>).
        </div>
      </AdvancedToolsSection>

      {/* iter 37 — IMMEX import modal (drag-and-drop file input + preview) */}
      {immexModalOpen && (
        <div
          data-testid="admin-immex-import-modal"
          onClick={closeImmexModal}
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(15, 15, 15, 0.72)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="cd-card"
            style={{
              width: "100%", maxWidth: 640,
              background: "var(--cream, #f7f3ec)",
              padding: 24,
              maxHeight: "90vh", overflowY: "auto",
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="overline flex items-center gap-2">
                <FileJson size={14} strokeWidth={1.6} />
                <span>Import IMMEX Registry Data</span>
              </div>
              <button
                type="button"
                onClick={closeImmexModal}
                disabled={importingImmex || immexPreviewing}
                aria-label="Close"
                data-testid="admin-immex-modal-close"
                style={{
                  background: "none", border: 0, cursor: "pointer",
                  color: "var(--ink-2)", padding: 4,
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Drop zone */}
            <div
              data-testid="admin-immex-dropzone"
              onDragOver={(e) => { e.preventDefault(); setImmexDragOver(true); }}
              onDragLeave={() => setImmexDragOver(false)}
              onDrop={onImmexDrop}
              onClick={() => immexFileInputRef.current?.click()}
              style={{
                border: `2px dashed ${immexDragOver ? "var(--orange)" : "var(--ink-2)"}`,
                background: immexDragOver ? "rgba(255,107,0,0.08)" : "transparent",
                padding: 28,
                textAlign: "center",
                cursor: "pointer",
                transition: "background-color 120ms ease, border-color 120ms ease",
              }}
            >
              <Upload size={22} strokeWidth={1.6} style={{ margin: "0 auto 8px" }} />
              <div className="font-mono text-sm">
                {immexFileName
                  ? <span data-testid="admin-immex-filename"><b>{immexFileName}</b></span>
                  : <span>Drop a <b>.json</b> file here, or click to browse</span>}
              </div>
              <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
                Expected: JSON array of records with a <code>slug</code> field.
              </div>
              <input
                ref={immexFileInputRef}
                type="file"
                accept=".json,application/json"
                data-testid="admin-immex-file-input"
                onChange={(e) => loadImmexFile(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </div>

            {immexError ? (
              <div
                className="mt-3 font-mono text-xs"
                style={{ color: "var(--red)" }}
                data-testid="admin-immex-error"
              >
                {immexError}
              </div>
            ) : null}

            {immexPreviewing ? (
              <div
                className="mt-3 font-mono text-xs flex items-center gap-2"
                style={{ color: "var(--ink-2)" }}
                data-testid="admin-immex-preview-loading"
              >
                <Loader2 size={14} className="animate-spin" /> Analyzing file…
              </div>
            ) : null}

            {immexPreview && !immexPreviewing ? (
              <div className="mt-4" data-testid="admin-immex-preview">
                <div className="overline mb-2">Preview · dry run</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3 font-mono text-xs">
                  <PreviewChip label="Records" value={immexPreview.received} tone="ink" testid="admin-immex-preview-received" />
                  <PreviewChip label="Will insert" value={immexPreview.inserted} tone="green" testid="admin-immex-preview-inserted" />
                  <PreviewChip label="Will update" value={immexPreview.updated} tone="orange" testid="admin-immex-preview-updated" />
                  <PreviewChip label="Skip · claimed" value={immexPreview.skipped_claimed} tone="red" testid="admin-immex-preview-skipped-claimed" />
                  <PreviewChip label="Skip · no slug" value={immexPreview.skipped_no_slug} tone="red" testid="admin-immex-preview-skipped-noslug" />
                  <PreviewChip label="Errors" value={immexPreview.errors?.length || 0} tone={(immexPreview.errors?.length || 0) > 0 ? "red" : "ink"} testid="admin-immex-preview-errors" />
                </div>

                {(immexPreview.sample_inserted?.length || immexPreview.sample_updated?.length || immexPreview.sample_skipped_claimed?.length) ? (
                  <div className="font-mono text-xs" style={{ color: "var(--ink-2)" }}>
                    {immexPreview.sample_inserted?.length ? (
                      <div className="mt-1">
                        <b style={{ color: "var(--green)" }}>Insert sample:</b>{" "}
                        {immexPreview.sample_inserted.slice(0, 5).join(", ")}
                        {immexPreview.sample_inserted.length > 5 ? `, +${immexPreview.sample_inserted.length - 5} more` : ""}
                      </div>
                    ) : null}
                    {immexPreview.sample_updated?.length ? (
                      <div className="mt-1">
                        <b style={{ color: "var(--orange)" }}>Update sample:</b>{" "}
                        {immexPreview.sample_updated.slice(0, 5).join(", ")}
                        {immexPreview.sample_updated.length > 5 ? `, +${immexPreview.sample_updated.length - 5} more` : ""}
                      </div>
                    ) : null}
                    {immexPreview.sample_skipped_claimed?.length ? (
                      <div className="mt-1">
                        <b style={{ color: "var(--red)" }}>Skip (claimed) sample:</b>{" "}
                        {immexPreview.sample_skipped_claimed.slice(0, 5).join(", ")}
                        {immexPreview.sample_skipped_claimed.length > 5 ? `, +${immexPreview.sample_skipped_claimed.length - 5} more` : ""}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-6 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeImmexModal}
                disabled={importingImmex || immexPreviewing}
                className="btn"
                data-testid="admin-immex-cancel-btn"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmImmexImport}
                disabled={
                  importingImmex ||
                  immexPreviewing ||
                  !immexPreview ||
                  ((immexPreview.inserted || 0) + (immexPreview.updated || 0)) === 0
                }
                className="btn btn-dark"
                data-testid="admin-immex-confirm-btn"
              >
                {importingImmex
                  ? <><Loader2 size={14} className="animate-spin" /> Importing…</>
                  : immexPreview
                    ? `Confirm · ${(immexPreview.inserted || 0) + (immexPreview.updated || 0)} record${((immexPreview.inserted || 0) + (immexPreview.updated || 0)) === 1 ? "" : "s"}`
                    : "Confirm import"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewChip({ label, value, tone = "ink", testid }) {
  const color = {
    green: "var(--green)",
    orange: "var(--orange)",
    red: "var(--red)",
    ink: "var(--ink)",
  }[tone] || "var(--ink)";
  return (
    <div
      data-testid={testid}
      style={{
        border: "1px solid var(--ink)",
        padding: "8px 10px",
        background: "rgba(255,255,255,0.4)",
      }}
    >
      <div className="overline" style={{ color: "var(--ink-2)" }}>{label}</div>
      <div className="font-display text-lg" style={{ color, lineHeight: 1 }}>
        {value ?? 0}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────
 * Command-Center sub-components
 * ────────────────────────────────────────────────────────────────── */

function MissionCard({ mission, loading }) {
  const style = PRIORITY_STYLE[mission.priority] || PRIORITY_STYLE.p3;
  return (
    <Link
      to={mission.cta_route}
      className="mt-8 cd-card p-6 md:p-7 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 relative overflow-hidden hover:opacity-95"
      style={{ background: style.bg, borderColor: style.stripe }}
      data-testid="admin-mission-card"
    >
      <div
        style={{
          position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
          background: style.stripe,
        }}
      />
      <div
        className="flex-shrink-0 w-12 h-12 flex items-center justify-center"
        style={{ background: style.stripe, color: "white", borderRadius: 2 }}
      >
        <Target size={22} strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="overline"
          style={{ color: style.stripe }}
          data-testid="admin-mission-priority"
        >
          MISSION · {style.label}
        </div>
        <div
          className="font-display text-2xl md:text-3xl mt-1 leading-tight"
          data-testid="admin-mission-title"
        >
          {loading ? "…" : mission.title}
        </div>
        <p className="mt-2 text-sm" style={{ color: "var(--ink-2)" }}>
          {mission.sub}
        </p>
      </div>
      <span
        className="btn btn-dark hidden md:inline-flex"
        data-testid="admin-mission-cta"
      >
        {mission.cta_label} <ArrowRight size={14} />
      </span>
    </Link>
  );
}

function StatCard({ icon: Icon, label, value, sub, accent, testid, to }) {
  const inner = (
    <div className="cd-card p-4 md:p-5 h-full" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span
          className="overline"
          style={{ color: accent || "var(--ink-2)" }}
        >
          {label}
        </span>
        <Icon size={14} strokeWidth={1.6} style={{ color: accent || "var(--ink-2)" }} />
      </div>
      <div className="font-display text-3xl md:text-4xl mt-2 leading-none">{value}</div>
      <div className="font-mono text-xs mt-1" style={{ color: "var(--ink-2)" }}>
        {sub || "—"}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block hover:opacity-95">{inner}</Link> : inner;
}

function Panel({ title, icon: Icon, link, accent, children, testid, className = "" }) {
  return (
    <div className={`cd-card p-5 md:p-6 ${className}`} data-testid={testid}>
      <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
        <div className="overline flex items-center gap-2" style={{ color: accent || "var(--ink)" }}>
          <Icon size={12} strokeWidth={1.6} />
          <span>{title}</span>
        </div>
        {link && (
          <Link
            to={link.to}
            className="font-mono text-xs underline-offset-2 hover:underline"
            style={{ color: "var(--ink-2)" }}
          >
            {link.label}
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function Empty({ label }) {
  return (
    <div
      className="py-6 text-center font-mono text-xs"
      style={{ color: "var(--ink-2)" }}
    >
      {label}
    </div>
  );
}

function StatusDot({ status }) {
  const color =
    status === "replied" ? "var(--green)" :
    status === "read" ? "#ea580c" :
    status === "archived" ? "var(--ink-2)" :
    "#dc2626"; // new
  return (
    <span
      className="dot mt-1.5 flex-shrink-0"
      style={{ background: color, width: 8, height: 8, borderRadius: "50%", display: "inline-block" }}
      title={status}
    />
  );
}

function formatAgo(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  if (ms < 60_000) return "now";
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  const mo = Math.floor(d / 30);
  return `${mo}mo`;
}


/* iter28 — compact failure-mode chip used in the Verification panel.
 * Zero values render in muted ink-2; non-zero in amber so the eye snaps
 * straight to the categories that actually need cleanup work. */
function BreakdownChip({ label, value, testid }) {
  const isZero = !value;
  return (
    <div
      className="border px-3 py-2"
      style={{
        borderColor: "var(--line)",
        background: isZero ? "transparent" : "#fff7ed",
      }}
      data-testid={testid}
    >
      <div className="overline" style={{ color: "var(--ink-2)" }}>{label}</div>
      <div
        className="text-lg font-semibold mt-0.5"
        style={{ color: isZero ? "var(--ink-2)" : "#9a3412" }}
      >
        {value ?? 0}
      </div>
    </div>
  );
}
